import { DotsThreeVertical } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * RowMenu — compact "⋮" dropdown for table row actions.
 *
 * Replaces inline strips of icon buttons (open link, copy, PDF, delete, etc.)
 * with a single trigger that opens a tidy menu. Cleaner tables, less overflow
 * on mobile, and groups dangerous actions away from frequent ones.
 *
 * Usage:
 *   <RowMenu items={[
 *     { label: "Abrir link", icon: ArrowSquareOut, onClick: ..., testid: "..." },
 *     { type: "separator" },
 *     { label: "Eliminar", icon: Trash, onClick: ..., danger: true, testid: "..." },
 *   ]} />
 *
 * Items support:
 *   - href + target (renders an <a> instead of <button>)
 *   - disabled
 *   - danger (red text)
 *   - type: "separator" | "label"
 */
export default function RowMenu({ items = [], testid, ariaLabel = "Acciones" }) {
  const visible = items.filter((i) => i && !i.hidden);
  if (visible.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          data-testid={testid || "row-menu-trigger"}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-100 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <DotsThreeVertical size={18} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]" onCloseAutoFocus={(e) => e.preventDefault()}>
        {visible.map((it, idx) => {
          if (it.type === "separator") return <DropdownMenuSeparator key={`sep-${idx}`} />;
          if (it.type === "label") {
            return (
              <DropdownMenuLabel key={`lbl-${idx}`} className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                {it.label}
              </DropdownMenuLabel>
            );
          }
          const Icon = it.icon;
          const dangerCls = it.danger
            ? "text-rose-600 dark:text-rose-400 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/40"
            : "";
          const content = (
            <>
              {Icon && <Icon size={14} className="mr-2 shrink-0" />}
              <span className="truncate">{it.label}</span>
            </>
          );
          if (it.href) {
            return (
              <DropdownMenuItem
                key={`item-${idx}`}
                asChild
                disabled={it.disabled}
                data-testid={it.testid}
                className={`text-xs cursor-pointer ${dangerCls}`}
              >
                <a href={it.href} target={it.target || "_blank"} rel="noopener noreferrer">{content}</a>
              </DropdownMenuItem>
            );
          }
          return (
            <DropdownMenuItem
              key={`item-${idx}`}
              onSelect={(e) => {
                e.preventDefault();
                it.onClick?.();
              }}
              disabled={it.disabled}
              data-testid={it.testid}
              className={`text-xs cursor-pointer ${dangerCls}`}
            >
              {content}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
